// ============================================================
// ConferenceCalls — /communications/conference-calls (PHASE_2M D4).
//
// "Conference Calls" is the VIDEO layer (Daily.co controlled rooms) — NOT
// "Daily Meetings" (the internal record under Operations). This surface now
// runs the FULL real flow:
//   status  — real provider state (/api/square/daily/status; no secrets)
//   create  — POST /api/square/daily/meetings (fail-closed server-side;
//             optional REAL portal/channel linking via context_type/id)
//   join    — POST /meetings/join → scoped short-lived token; opens the
//             backend-returned room URL (never constructed client-side)
//   copy    — copies the backend-returned room URL only
//   cancel  — POST /meetings/cancel (two-step confirm, audited server-side)
//
// Truth rules: no fake room, no fake join URL, no fake active call; a
// blocked/failed create surfaces the server's exact reason; DAILY_API_KEY
// never reaches the browser. Receipts: the v17 backend WRITES meeting
// receipts + OLIVIUM audit rows on create/join/cancel — a receipt FEED here
// is pending a list route (labeled precisely, not "BACKEND_DEFERRED").
// ============================================================

import { useState } from 'react'
import CommsFrame, { MessageCenterNav, StateBlock, card, sectionTitle, btn, inputStyle, INK, MONO } from './CommsShell'
import { MeetingIcon, CopyIcon, OpenIcon, TrashIcon, PlusIcon, ScheduleIcon } from '../shell/icons'
import {
  conferenceProviderStatus, CALL_MODES, CALL_STATUS_META,
  type ConferenceCallStatusState, type CreateCallInput,
} from './conferenceCallsData'
import type { Meeting } from '../../../api/client'

export interface ConferenceCallsProps extends ConferenceCallStatusState {
  meetings?: Meeting[]
  meetingsLoading?: boolean
  meetingsError?: string | null
  /** Real create — resolves with the server's exact outcome. */
  onCreate?: (input: CreateCallInput) => Promise<{ ok: true } | { ok: false; reason: string; message?: string; required_approval?: string | null }>
  /** Real join — opens the backend-returned URL; resolves false with the exact reason. */
  onJoin?: (meetingId: string) => Promise<{ ok: boolean; reason?: string }>
  onCancel?: (meetingId: string) => Promise<void>
  onCopyLink?: (meeting: Meeting) => void
  onScheduleMeeting?: () => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onNavTop?: (to: string) => void
  onOpenMessageCenter?: () => void
  onOpenTeam?: () => void
  onOpenTasks?: () => void
}

function Pill({ label, tone }: { label: string; tone: 'ready' | 'deferred' | 'unknown' }) {
  const c = tone === 'ready' ? '#059669' : tone === 'deferred' ? '#b8862a' : INK.muted
  return <span style={{ display: 'inline-block', fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', fontFamily: MONO, color: c, padding: '3px 8px', borderRadius: 5, background: c + '18', border: `1px solid ${c}33` }}>{label}</span>
}

function StatusChip({ status }: { status: Meeting['status'] }) {
  const m = CALL_STATUS_META[status] || { label: String(status).toUpperCase(), accent: INK.muted }
  return <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.08em', fontFamily: MONO, color: m.accent, padding: '2px 7px', borderRadius: 4, background: m.accent + '15', border: `1px solid ${m.accent}33`, whiteSpace: 'nowrap' }}>{m.label}</span>
}

const iconBtn = (accent: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8, cursor: 'pointer',
  background: accent + '14', border: `1px solid ${accent}38`, color: accent, fontSize: 11, fontWeight: 800, fontFamily: MONO,
})

function CallRow({ m, onJoin, onCancel, onCopyLink }: { m: Meeting; onJoin?: ConferenceCallsProps['onJoin']; onCancel?: ConferenceCallsProps['onCancel']; onCopyLink?: (m: Meeting) => void }) {
  const [busy, setBusy] = useState<'join' | 'cancel' | null>(null)
  const [armed, setArmed] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const joinable = m.status === 'created' || m.status === 'token_ready' || m.status === 'active'
  return (
    <div style={{ padding: '11px 13px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <MeetingIcon size={15} color="#059669" still={m.status !== 'active'} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: INK.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</span>
        <StatusChip status={m.status} />
      </div>
      <div style={{ margin: '5px 0 9px', fontSize: 10, fontFamily: MONO, color: INK.faint }}>
        {m.meeting_mode.replace(/_/g, ' ')} · room {m.daily_room_name}
        {m.context_type !== 'manual' && m.context_id ? ` · linked ${m.context_type.replace(/_/g, ' ')}` : ''}
        {' · expires '}{m.expires_at.slice(0, 16).replace('T', ' ')}
      </div>
      {failure && <p style={{ margin: '0 0 8px', fontSize: 10.5, color: '#f87171', fontFamily: MONO }}>{failure}</p>}
      {joinable && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <button type="button" disabled={busy !== null} style={iconBtn('#059669')} onClick={async () => {
            setBusy('join'); setFailure(null)
            const r = await onJoin?.(m.id)
            if (r && !r.ok) setFailure(`Join refused — ${r.reason || 'unknown reason'} (server-reported; nothing faked).`)
            setBusy(null)
          }}>
            <OpenIcon size={11} /> {busy === 'join' ? 'GETTING TOKEN…' : 'JOIN'}
          </button>
          <button type="button" style={iconBtn('#0891b2')} title="Copies the backend-returned room URL (join still requires a scoped token)." onClick={() => onCopyLink?.(m)}>
            <CopyIcon size={11} /> COPY LINK
          </button>
          <button type="button" disabled={busy !== null} style={iconBtn('#dc2626')} onClick={async () => {
            if (!armed) { setArmed(true); return }
            setBusy('cancel'); setFailure(null)
            try { await onCancel?.(m.id) } catch { setFailure('Cancel failed — the server rejected it; the room is unchanged.') }
            setBusy(null); setArmed(false)
          }}>
            <TrashIcon size={11} /> {armed ? 'CONFIRM CANCEL' : busy === 'cancel' ? 'CANCELLING…' : 'CANCEL'}
          </button>
        </div>
      )}
    </div>
  )
}

export default function ConferenceCalls(props: ConferenceCallsProps) {
  const {
    status, loading, error, authRequired, meetings = [], meetingsLoading, meetingsError,
    onCreate, onJoin, onCancel, onCopyLink, onScheduleMeeting,
    onHome, onBackCommandCenter, onNavTop, onOpenMessageCenter, onOpenTeam, onOpenTasks,
  } = props
  const prov = conferenceProviderStatus(status)

  const [title, setTitle] = useState('')
  const [mode, setMode] = useState(CALL_MODES[0].value)
  const [duration, setDuration] = useState('30')
  const [portalId, setPortalId] = useState('')
  const [creating, setCreating] = useState(false)
  const [createFailure, setCreateFailure] = useState<string | null>(null)

  const canCreate = prov.tone === 'ready' && title.trim().length > 0 && !creating

  const submit = async () => {
    if (!canCreate || !onCreate) return
    setCreating(true); setCreateFailure(null)
    const r = await onCreate({
      title: title.trim(), meeting_mode: mode,
      duration_minutes: Math.max(5, Math.min(240, parseInt(duration, 10) || 30)),
      portal_id: portalId.trim() || undefined,
    })
    if (r.ok) { setTitle(''); setPortalId('') }
    else setCreateFailure(`Room NOT created — ${r.reason}${r.required_approval ? ` (requires approval: ${r.required_approval})` : ''}${r.message ? ` — ${r.message}` : ''}`)
    setCreating(false)
  }

  const active = meetings.filter((m) => m.status !== 'cancelled' && m.status !== 'ended' && m.status !== 'failed')
  const recent = meetings.filter((m) => m.status === 'cancelled' || m.status === 'ended' || m.status === 'failed').slice(0, 5)

  const center = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h3 style={{ ...sectionTitle, margin: 0, flex: 1 }}>Provider Status · Daily.co video conferencing</h3>
          {!loading && !authRequired && !error && <Pill label={prov.badge} tone={prov.tone} />}
        </div>
        {loading || authRequired || error ? (
          <StateBlock loading={loading} error={error} authRequired={authRequired} emptyLabel="" />
        ) : (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: prov.tone === 'ready' ? '#5fd0a0' : INK.primary, marginBottom: 5 }}>{prov.label}</div>
            <p style={{ margin: 0, fontSize: 11.5, color: INK.muted, lineHeight: 1.6 }}>{prov.detail}</p>
          </div>
        )}
      </div>

      <div style={card}>
        <h3 style={sectionTitle}>Create Conference Call</h3>
        {prov.tone === 'ready' ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: INK.muted, fontFamily: MONO, marginBottom: 5 }}>TITLE / PURPOSE *</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Silver Key workspace pitch" style={inputStyle} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: INK.muted, fontFamily: MONO, marginBottom: 5 }}>MODE</span>
                <select value={mode} onChange={(e) => setMode(e.target.value)} style={inputStyle}>
                  {CALL_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: INK.muted, fontFamily: MONO, marginBottom: 5 }}>DURATION (MIN)</span>
                <input value={duration} onChange={(e) => setDuration(e.target.value)} inputMode="numeric" style={inputStyle} />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: INK.muted, fontFamily: MONO, marginBottom: 5 }}>LINK TO PROJECT PORTAL (OPTIONAL — portal id)</span>
                <input value={portalId} onChange={(e) => setPortalId(e.target.value)} placeholder="Stored on the meeting record (context_type=project_portal)" style={inputStyle} />
              </label>
            </div>
            {createFailure && <p style={{ margin: '10px 0 0', fontSize: 11, color: '#f87171', fontFamily: MONO }}>{createFailure}</p>}
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={submit} disabled={!canCreate} style={{ ...btn, opacity: canCreate ? 1 : 0.55, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <PlusIcon size={12} /> {creating ? 'Creating controlled room…' : 'Create Conference Call'}
              </button>
            </div>
            <p style={{ margin: '9px 2px 0', fontSize: 10, color: INK.faint, fontFamily: MONO }}>
              Creates a REAL controlled Daily room server-side (audited + receipted). The browser never sees DAILY_API_KEY; joining uses a scoped, short-lived token.
            </p>
          </>
        ) : (
          <StateBlock empty emptyLabel={`Creating a room is disabled — ${prov.label}. This gate reflects the real provider status; no room is ever faked.`} />
        )}
      </div>

      <div style={card}>
        <h3 style={sectionTitle}>Rooms</h3>
        {authRequired || meetingsLoading || meetingsError ? (
          <StateBlock loading={meetingsLoading} error={meetingsError} authRequired={authRequired} emptyLabel="" />
        ) : active.length === 0 ? (
          <StateBlock empty emptyLabel="No active rooms — this list is live and genuinely empty. Create a call above (provider-gated); nothing is fabricated." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {active.map((m) => <CallRow key={m.id} m={m} onJoin={onJoin} onCancel={onCancel} onCopyLink={onCopyLink} />)}
          </div>
        )}
        {recent.length > 0 && (
          <>
            <h4 style={{ margin: '14px 0 8px', fontSize: 9.5, letterSpacing: '0.14em', fontWeight: 800, fontFamily: MONO, color: INK.faint }}>RECENT (ENDED / CANCELLED)</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: 0.75 }}>
              {recent.map((m) => <CallRow key={m.id} m={m} onCopyLink={onCopyLink} />)}
            </div>
          </>
        )}
      </div>
    </div>
  )

  const rightRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={card}>
        <h3 style={sectionTitle}>Scheduling</h3>
        <button type="button" onClick={onScheduleMeeting} style={{ ...btn, width: '100%', textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <ScheduleIcon size={12} /> Schedule Meeting (Daily Meetings record)
        </button>
        <p style={{ margin: '8px 2px 0', fontSize: 10, color: INK.faint, fontFamily: MONO }}>
          Opens the internal Daily Meetings workflow (real persisted records under Operations). Cal.com/external scheduling: INTEGRATION_DEFERRED (no provider wired).
        </p>
      </div>
      <div style={card}>
        <h3 style={sectionTitle}>Receipts</h3>
        <p style={{ margin: 0, fontSize: 11, color: INK.muted, lineHeight: 1.6 }}>
          Create / join / cancel are receipted server-side (sq_meeting_receipts) and OLIVIUM-audited on every action. A receipt FEED here is pending a list route — the records exist; only this view of them does not yet.
        </p>
      </div>
      <div style={card}>
        <h3 style={sectionTitle}>Linking</h3>
        <p style={{ margin: 0, fontSize: 11, color: INK.muted, lineHeight: 1.6 }}>
          Portal linking is REAL — the created meeting stores context_type=project_portal + the portal id. Channel linking uses context_type=comms_channel. Surfacing linked calls inside the portal/channel views lands with the comms cockpit.
        </p>
      </div>
    </div>
  )

  return (
    <CommsFrame
      activeTop="communications"
      breadcrumb={['COMMAND CENTER', 'COMMUNICATIONS', 'CONFERENCE CALLS']}
      title="Conference Calls"
      statusBadge={<span style={{ marginLeft: 8, fontSize: 10, color: INK.faint, fontFamily: MONO }}>Daily.co video</span>}
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      onNavTop={onNavTop}
      leftRail={<MessageCenterNav active="conference_calls" onOpenHome={onOpenMessageCenter} onOpenConferenceCalls={() => { /* current */ }} onOpenTeam={onOpenTeam} onOpenTasks={onOpenTasks} />}
      center={center}
      rightRail={rightRail}
    />
  )
}
